import { useEffect } from "react";

import { Action, Category } from "@/constants/events";
import { SnackBarMessages, useSnackBar } from "@/context/SnackBarContext";
import { Coordinates } from "@/custom-types/Coordinates";
import { Dispensary } from "@/custom-types/Dispensary";
import MenuItemFilterSection from "@/custom-types/MenuItemFilterSection";
import { MenuItemV2 } from "@/custom-types/MenuItemV2";
import { useEventTracker } from "@/hooks/useEventTracker";
import useQueryParamState from "@/hooks/useQueryParamState";
import { useSelector } from "@/redux/hooks";
import { getIsValidStreetAddress } from "@/redux/selectors/location";

import Button from "@/components/botanic/Button";
import Carousel from "@/components/botanic/Carousel";
import MenuItemCard from "@/components/botanic/MenuItemCard";
import DispensaryFulfillmentInfo from "@/components/DispensaryFulfillmentInfo";
import { useFilterQueryParams } from "@/components/MenuFilters/v2/hooks";
import { MenuItemCardActions } from "@/components/Shared/MenuItemCarousel/MenuItemCardActions";
import { useGetMerchCampaignMenuItems } from "@/components/ShopCoreMenuItemResults/hooks/useGetMerchCampaignMenuItems";
import TrackImpression from "@/components/TrackImpression";

import SponsoredMenuItemLoadingSkeleton from "./SponsoredMenuItemLoadingSkeleton";

const DPP = "delivery-platinum-placement";
const RADIUS = 20;

export const SponsoredMenuItemResults = ({
  className,
  coordinates,
  isDeliveryFulfillmentMode,
  onShowAddressCaptureModal,
}: {
  className?: string;
  coordinates: Coordinates;
  isDeliveryFulfillmentMode?: boolean;
  onShowAddressCaptureModal: () => void;
}) => {
  const [q, _setQ] = useQueryParamState("q");
  const {
    appliedFilters,
    actions: { toggleFilter },
  } = useFilterQueryParams();
  const filtersApplied = Object.keys(appliedFilters).length > 0;
  const { addSnackBarItem } = useSnackBar();
  const { publishEvent } = useEventTracker();
  const isValidAddress = useSelector(getIsValidStreetAddress);
  const hasFiltersButNoAddress = filtersApplied && !isValidAddress;

  const { menuItems, menuItemsLoading, search, sponsoredDispensaryMap } =
    useGetMerchCampaignMenuItems({
      campaignType: DPP,
      locationData: { coordinates, radius: RADIUS },
    });

  useEffect(() => {
    search(appliedFilters, q ? String(q) : undefined);
  }, [appliedFilters, q]);

  if (
    !isDeliveryFulfillmentMode ||
    (menuItems?.length === 0 && !menuItemsLoading)
  ) {
    return null;
  }

  const getmerchandisingCampaignId = (menuItem: MenuItemV2) => {
    const dpp = sponsoredDispensaryMap?.[
      menuItem?.dispensaryId
    ].merchandisedCampaigns?.filter((campaign) => {
      return campaign.unitName === "delivery-platinum-placement";
    });

    return dpp?.[0]?.id;
  };

  return (
    <>
      <div className={className} data-testid="dpp-featured-menu-items">
        <Carousel cardsPerSlide={3}>
          {menuItems?.length > 0 && !menuItemsLoading
            ? menuItems.slice(0, 3).map((menuItem, index) => (
                <TrackImpression
                  key={`dsp-${menuItem.id}`}
                  className="h-full"
                  trackingFunction={() =>
                    publishEvent({
                      action: Action.impression,
                      brandId: menuItem?.brand?.id,
                      category: Category.deliveryGateFlow,
                      dispensaryId: menuItem?.dispensaryId,
                      label: "menu item offer card",
                      menuItemId: menuItem.menuItemId,
                      merchandisingCampaignId:
                        getmerchandisingCampaignId(menuItem),
                      productId: menuItem.product?.id,
                      strainId: menuItem.strain?.id,
                      trayNumber: index + 1,
                    })
                  }
                >
                  <MenuItemCard
                    menuItem={menuItem}
                    isFeatured={true}
                    handleCardClick={(e) => {
                      if (hasFiltersButNoAddress) {
                        e?.preventDefault();
                        onShowAddressCaptureModal();
                      } else {
                        publishEvent({
                          action: Action.click,
                          brandId: menuItem?.brand?.id,
                          category: Category.deliveryGateFlow,
                          dispensaryId: menuItem?.dispensaryId,
                          label: "menu item offer card",
                          menuItemId: menuItem.menuItemId,
                          merchandisingCampaignId:
                            getmerchandisingCampaignId(menuItem),
                          productId: menuItem.product?.id,
                          strainId: menuItem.strain?.id,
                          trayNumber: index + 1,
                        });
                      }
                    }}
                    renderFulfillmentInfo={(dispensary: Dispensary) => (
                      <DispensaryFulfillmentInfo
                        dispensary={dispensary}
                        showCarouselInfo={true}
                      />
                    )}
                  >
                    {/* Replace normal MIC Actions (like add to cart)
                  with a dummy that instead prompts for accurate address */}
                    {hasFiltersButNoAddress ? (
                      <Button
                        data-testid="add-to-cart-button"
                        iconFilePath="bag.svg"
                        onClick={onShowAddressCaptureModal}
                        className="w-full"
                      >
                        add to cart
                      </Button>
                    ) : (
                      <MenuItemCardActions
                        merchandisingCampaignId={getmerchandisingCampaignId(
                          menuItem,
                        )}
                        menuItem={menuItem}
                        onReservation={() => {
                          const dispensaryNameFilterValue =
                            appliedFilters[
                              MenuItemFilterSection.DispensaryName
                            ];
                          const isDispensaryFiltered = Array.isArray(
                            dispensaryNameFilterValue,
                          )
                            ? dispensaryNameFilterValue.includes(
                                menuItem.dispensary.name,
                              )
                            : dispensaryNameFilterValue ===
                              menuItem.dispensary.name;
                          if (!isDispensaryFiltered) {
                            addSnackBarItem(
                              SnackBarMessages.resultsFilteredToDispensary,
                            );
                            toggleFilter(
                              MenuItemFilterSection.DispensaryName,
                              menuItem.dispensary.name,
                            );
                          }
                        }}
                      />
                    )}
                  </MenuItemCard>
                </TrackImpression>
              ))
            : ["a", "b", "c"].map((_x, index) => (
                <SponsoredMenuItemLoadingSkeleton key={index} />
              ))}
        </Carousel>
      </div>
      <hr className="border-light-grey mt-md mb-xl" />
    </>
  );
};
